import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import MSelectView from './MSelect.view';

interface Props<ValueType extends string | number> {
  className?: string;
  placeholder?: string;
  value?: ValueType;
  onChange?: (value: ValueType) => void;
}

const MSelect = <ValueType extends string | number>(props: React.PropsWithChildren<Props<ValueType>>): JSX.Element => {
  const listRef = useRef<HTMLUListElement>(null);

  // List of objects of all <option> inserted
  const options = React.Children.map(
    props.children as React.DetailedReactHTMLElement<React.HTMLAttributes<HTMLOptionElement>, HTMLOptionElement>[],
    (child: React.DetailedReactHTMLElement<React.HTMLAttributes<HTMLOptionElement>, HTMLOptionElement>) => {
      return {
        label: child.props.children,
        value: child.props['value'],
      };
    },
  );

  // Find the object of options linked to forced value from props
  const valueObject = useMemo(() => {
    return options.find((option) => option.value === props.value) || null;
  }, [props.value]);

  const [listState, setListState] = useState<boolean>(false);
  const [selectedState, setSelectedState] = useState<{ label: React.ReactNode; value: ValueType } | null>(valueObject);

  const setSelectedOption = useCallback(
    (value: ValueType, index: number) => {
      props.onChange?.(value);
      setSelectedState(() => options[index]);
      setListState(() => false);
    },
    [props.onChange, setListState],
  );

  useEffect(() => {
    if (listState && listRef.current) {
      const checkClick = (event: MouseEvent) => {
        if (listRef.current && listRef.current !== event.target && !listRef.current.contains(event.target as HTMLElement)) {
          setListState(() => false);
        }
      };

      document.addEventListener('click', checkClick);

      return () => {
        document.removeEventListener('click', checkClick);
      };
    }
  }, [listRef.current, listState, setListState]);

  useEffect(() => {
    setSelectedState(() => valueObject);
  }, [valueObject]);

  return (
    <MSelectView
      className={props.className}
      label={selectedState?.label ?? null}
      listRef={listRef}
      showList={listState}
      onButtonClick={() => setListState(() => true)}
      onOptionClick={setSelectedOption}
      placeholder={props.placeholder}
      selected={selectedState?.value ?? null}
      options={options}
    >
      {props.children}
    </MSelectView>
  );
};

MSelect.displayName = 'MSelect';
MSelect.defaultProps = {};

export default MSelect;
